import type { TabName } from '@/types';

// ============================================================================
// GAME CONFIGURATION
// ============================================================================

export interface StatVisibilityConfig {
    visible?: boolean;
    requiresUpgrade?: string;
}

export const GAME_CONFIG = {
    // Tab configuration
    TABS: [
        { id: 'pipeline' as TabName, name: 'Pipeline', icon: '🔧' },
        { id: 'team' as TabName, name: 'Team', icon: '👥' },
        { id: 'dataUpgrades' as TabName, name: 'Data Upgrades', icon: '📊' },
        { id: 'mlUpgrades' as TabName, name: 'ML Upgrades', icon: '🤖' },
        { id: 'achievements' as TabName, name: 'Achievements', icon: '🏆' }
    ],

    // Stats visibility configuration
    STATS_VISIBILITY: {
        // Always visible stats
        rawData: { visible: true } as StatVisibilityConfig,
        money: { visible: true } as StatVisibilityConfig,
        rawDataPerSec: { visible: true } as StatVisibilityConfig,
        incomePerSec: { visible: true } as StatVisibilityConfig,

        // Stats that require HR upgrade (when you can start hiring and building infrastructure)
        infrastructureExpensesPerSec: { requiresUpgrade: 'hr' } as StatVisibilityConfig,
        talentExpensesPerSec: { requiresUpgrade: 'hr' } as StatVisibilityConfig,
        totalExpensesPerSec: { requiresUpgrade: 'hr' } as StatVisibilityConfig,
        netProfitPerSec: { requiresUpgrade: 'hr' } as StatVisibilityConfig,
        infrastructureReliability: { visible: true } as StatVisibilityConfig,

        // Stats that require specific upgrades
        cleanData: { requiresUpgrade: 'dataCleaning' } as StatVisibilityConfig,
        cleanDataPerSec: { requiresUpgrade: 'dataCleaning' } as StatVisibilityConfig,
        insights: { requiresUpgrade: 'dataCleaning' } as StatVisibilityConfig,
        insightsPerSec: { requiresUpgrade: 'dataCleaning' } as StatVisibilityConfig,
        features: { requiresUpgrade: 'ml' } as StatVisibilityConfig,
        featuresPerSec: { requiresUpgrade: 'ml' } as StatVisibilityConfig,
        models: { requiresUpgrade: 'modelTraining' } as StatVisibilityConfig,
        modelsPerSec: { requiresUpgrade: 'modelTraining' } as StatVisibilityConfig,
        reasoningEngines: { requiresUpgrade: 'reasoningSystems' } as StatVisibilityConfig,
        reasoningEnginesPerSec: { requiresUpgrade: 'reasoningSystems' } as StatVisibilityConfig,
        agiProgress: { requiresUpgrade: 'agi' } as StatVisibilityConfig
    },

    // Pipeline stage configuration


    // Upgrade categories
    UPGRADE_CATEGORIES: {
        dataUpgrades: [
            'automation', 'dataCleaning', 'hr', 'dataStorage', 'iotNetworks',
            'streaming', 'dataMarketplace', 'cloud', 'dataGovernance', 'redundancy',
            'dataCompression',
            'distributedStorage', 'distributedComputing', 'ml'
        ],
        mlUpgrades: [
            'modelTraining', 'advancedAI', 'reasoningSystems', 'agi'
        ]
    },

    // Game constants
    CONSTANTS: {
        RAW_DATA_SELL_VALUE: 0.5,
        CLEAN_DATA_SELL_VALUE: 1,
        INSIGHTS_SELL_VALUE: 5,
        FEATURES_SELL_VALUE: 10,
        MODELS_SELL_VALUE: 100,
        GAME_LOOP_INTERVAL: 1000, // Game tick interval in milliseconds (1000ms = 1 second)
        SAVE_INTERVAL: 5000,
        MIN_FAILURE_RATE: 0.0,
        MAX_FAILURE_RATE: 0.7,
        EVENT_PROBABILITY: 0.01 // Probability of random event per game tick (0.01 = 1% per tick)
    },

    // Personnel-to-Infrastructure Ratios
    PERSONNEL_RATIOS: {
        // Base ratios (before upgrades)
        BASE: {
            dataEngineer: {
                etlPipelines: 3,
                streamingSystems: 3
            },
            mlEngineer: {
                featurePipelines: 5   // 1 ML engineer per 2 feature pipeline
            },
            aiResearcher: {
                trainingPipelines: 1  // 1 AI researcher per training pipeline
            },
            reasoningSpecialist: {
                // Reasoning specialists produce reasoning engines as a resource, not manage infrastructure
            }
        },

        // Cloud migration improves engineer efficiency
        CLOUD_MIGRATION: {
            dataEngineer: {
                etlPipelines: 3,      // 1 data engineer can manage 3 ETL pipelines
                streamingSystems: 2   // 1 data engineer can manage 2 streaming systems
            },
            mlEngineer: {
                featurePipelines: 3   // 1 ML engineer can manage 3 feature pipelines
            }
        }
    }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export function getUpgradesByCategory(category: 'dataUpgrades' | 'mlUpgrades') {
    return GAME_CONFIG.UPGRADE_CATEGORIES[category];
}

export function getPersonnelRatio(
    personnelType: 'dataEngineer' | 'mlEngineer' | 'aiResearcher' | 'reasoningSpecialist',
    infrastructureType: string,
    gameState: { upgrades: Record<string, boolean> }
): number {
    // Default ratio
    let ratio = 1;

    // Get base ratio
    const baseRatios = GAME_CONFIG.PERSONNEL_RATIOS.BASE[personnelType];
    if (baseRatios && infrastructureType in baseRatios) {
        ratio = (baseRatios as any)[infrastructureType];
    }

    // Apply cloud migration bonus for data engineers and ML engineers
    if (gameState.upgrades.cloud) {
        if (personnelType === 'dataEngineer') {
            const cloudRatios = GAME_CONFIG.PERSONNEL_RATIOS.CLOUD_MIGRATION.dataEngineer;
            if (cloudRatios && infrastructureType in cloudRatios) {
                ratio = (cloudRatios as any)[infrastructureType];
            }
        } else if (personnelType === 'mlEngineer') {
            const cloudRatios = GAME_CONFIG.PERSONNEL_RATIOS.CLOUD_MIGRATION.mlEngineer;
            if (cloudRatios && infrastructureType in cloudRatios) {
                ratio = (cloudRatios as any)[infrastructureType];
            }
        }
    }

    // Reasoning specialists don't manage infrastructure, they produce reasoning engines as a resource
    if (personnelType === 'reasoningSpecialist') {
        return 0; // No infrastructure management
    }

    return ratio;
}

export function canBuildInfrastructure(
    infrastructureType: string,
    personnelType: string,
    currentPersonnel: number,
    currentInfrastructure: number,
    gameState: { upgrades: Record<string, boolean> }
): boolean {
    const ratio = getPersonnelRatio(
        personnelType as any,
        infrastructureType as any,
        gameState
    );

    // If ratio is 0, it means this personnel type doesn't manage infrastructure
    if (ratio === 0) {
        return true; // Can always build infrastructure that doesn't require this personnel type
    }

    // Check if we have enough personnel to manage the new infrastructure
    const requiredPersonnel = Math.ceil((currentInfrastructure + 1) / ratio);
    return currentPersonnel >= requiredPersonnel;
} 